import React, { useEffect, useState, useRef } from 'react';
import Plot from 'react-plotly.js';
import CategoryIcon from '../CategoryIcon';
import { authHeaders } from '../../utils/driverPrivacy';
import lineChartIcon from '../../assets/line-chart-white.png';

const API_URL = 'https://iracing6-backend.herokuapp.com';

const categoryColors = {
  road: '#1f77b4',
  sports_car: '#ff7f0e',
  formula_car: '#2ca02c',
  oval: '#d62728',
  dirt_road: '#9467bd',
  dirt_oval: '#8c564b',
};

const CATEGORIES = ['sports_car', 'formula_car', 'oval', 'dirt_road', 'dirt_oval'];

const formatCategoryName = (cat) =>
  cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

function CareerStatsWidget({ config, onConfigChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState(config.view || 'chart');
  const isInitialMount = useRef(true);

  const custId = config.custId || '';

  useEffect(() => {
    if (!custId) {
      setLoading(false);
      setError('No Customer ID configured');
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/member-career-stats/career/${custId}`, { headers: authHeaders() });

        if (response.status === 403) {
          setError("This driver hasn't opted in to sharing their profile. Use your own Customer ID, or a driver who has opted in.");
          setLoading(false);
          return;
        }

        if (response.status === 202 || response.status === 500) {
          setError('User queued for scan. Check back later.');
          setLoading(false);
          return;
        }

        if (!response.ok) throw new Error('Failed to fetch career stats');
        const result = await response.json();
        setData(result);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [custId]);

  // Persist view selection to backend config
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (onConfigChange) {
      onConfigChange({ ...config, view });
    }
  }, [view]);

  if (loading) {
    return <div className="widget-loading"><div className="spinner-small"></div></div>;
  }

  if (error) {
    return <div className="widget-error">{error}</div>;
  }

  if (!data) {
    return <div className="widget-error">No data available</div>;
  }

  // View selector bar
  const renderViewSelector = () => (
    <div className="csw-view-selector">
      <span className="spw-filter-label">Graph:</span>
      <button
        className={`spw-toggle-btn ${view === 'chart' ? 'spw-toggle-active' : ''}`}
        onClick={() => setView('chart')}
        title="iRating Chart"
      >
        <img src={lineChartIcon} alt="Chart" width={14} height={14} />
      </button>
      <span className="spw-filter-label">Stats:</span>
      {CATEGORIES.map((cat) => (
        <button
          key={cat}
          className={`spw-toggle-btn ${view === cat ? 'spw-toggle-active' : ''}`}
          onClick={() => setView(cat)}
          title={formatCategoryName(cat)}
        >
          <CategoryIcon category={cat} size={14} />
        </button>
      ))}
    </div>
  );

  // Render category stats view
  if (view !== 'chart') {
    const catData = data[view];
    if (!catData || catData.starts === undefined) {
      return (
        <div className="csw-container">
          {renderViewSelector()}
          <div className="widget-error">No {formatCategoryName(view)} data</div>
        </div>
      );
    }

    const iRatingValue = catData.iRating?.value || 0;
    const startsPerWin = catData.wins > 0
      ? (catData.starts / catData.wins).toFixed(1)
      : '—';
    const winPct = catData.starts > 0
      ? ((catData.wins / catData.starts) * 100).toFixed(1) + '%'
      : '0%';

    return (
      <div className="csw-container">
        {renderViewSelector()}
        <div className="csw-stats-body">
          <div className="csw-player-name">{data.display_name || `#${custId}`}</div>
          <div className="csw-irating-hero">
            <span className="csw-irating-value" style={{ color: categoryColors[view] }}>
              {iRatingValue}
            </span>
            <span className="csw-irating-label">iRating</span>
          </div>
          <div className="csw-stats-grid">
            <div className="csw-stat">
              <div className="csw-stat-value">{catData.starts}</div>
              <div className="csw-stat-label">Starts</div>
            </div>
            <div className="csw-stat">
              <div className="csw-stat-value">{catData.wins}</div>
              <div className="csw-stat-label">Wins</div>
            </div>
            <div className="csw-stat">
              <div className="csw-stat-value">{winPct}</div>
              <div className="csw-stat-label">Win %</div>
            </div>
            <div className="csw-stat">
              <div className="csw-stat-value">{startsPerWin}</div>
              <div className="csw-stat-label">Starts/Win</div>
            </div>
            <div className="csw-stat">
              <div className="csw-stat-value">{catData.avg_incidents?.toFixed(2) ?? '—'}</div>
              <div className="csw-stat-label">Avg Inc</div>
            </div>
            <div className="csw-stat">
              <div className="csw-stat-value">{catData.laps_led_percentage ?? 0}%</div>
              <div className="csw-stat-label">Laps Led</div>
            </div>
            <div className="csw-stat">
              <div className="csw-stat-value">{catData.poles}</div>
              <div className="csw-stat-label">Poles</div>
            </div>
            <div className="csw-stat">
              <div className="csw-stat-value">{catData.top5}</div>
              <div className="csw-stat-label">Top 5s</div>
            </div>
            <div className="csw-stat">
              <div className="csw-stat-value">{catData.laps?.toLocaleString()}</div>
              <div className="csw-stat-label">Laps</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render iRating chart view (original)
  const allCategories = [...CATEGORIES, 'road'];
  const traces = allCategories
    .filter(cat => data[cat]?.iRating_chart?.data?.length > 0)
    .map(category => {
      const chartData = data[category].iRating_chart.data;
      return {
        x: chartData.map(item => item.when),
        y: chartData.map(item => item.value),
        type: 'scatter',
        mode: 'lines',
        name: formatCategoryName(category),
        line: {
          color: categoryColors[category],
          width: 1.5,
        },
      };
    });

  if (traces.length === 0) {
    return (
      <div className="csw-container">
        {renderViewSelector()}
        <div className="widget-error">No iRating history available</div>
      </div>
    );
  }

  const layout = {
    margin: { l: 40, r: 10, t: 25, b: 25 },
    plot_bgcolor: 'transparent',
    paper_bgcolor: 'transparent',
    title: {
      text: data.display_name || `#${custId}`,
      font: { size: 11, color: '#ddd' },
      x: 0.5,
      y: 0.98,
    },
    xaxis: {
      color: '#aaa',
      gridcolor: '#333',
      showticklabels: false,
      fixedrange: true,
    },
    yaxis: {
      title: 'iRating',
      titlefont: { size: 9, color: '#aaa' },
      color: '#aaa',
      gridcolor: '#333',
      fixedrange: true,
    },
    font: {
      family: 'Questrial, sans-serif',
      size: 9,
      color: '#ddd',
    },
    legend: {
      orientation: 'h',
      y: -0.15,
      font: { size: 8 },
    },
    showlegend: true,
    autosize: true,
  };

  return (
    <div className="csw-container">
      {renderViewSelector()}
      <div className="widget-chart">
        <Plot
          data={traces}
          layout={layout}
          config={{ displayModeBar: false, responsive: true }}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler={true}
        />
      </div>
    </div>
  );
}

export default CareerStatsWidget;
